import { useCallback, useEffect, useRef, useState } from 'react';
import { audio, LOW_MOONLIGHT_THRESHOLD } from '../audio/AudioEngine.ts';
import { firstLight } from '../content/rooms/01-first-light.ts';
import { KeyboardSource } from '../input/KeyboardSource.ts';
import { createCharacterAxie, type PlayableCharacter } from '../render/AxieCharacter.ts';
import { Renderer } from '../render/Renderer.ts';
import { DT } from '../sim/constants.ts';
import { Simulation } from '../sim/sim.ts';
import {
  FailReason,
  Outcome,
  STARTER_LOADOUT,
  type DoorPrompt,
  type FailReasonName,
  type Loadout,
  type OutcomeName,
  type RoomDef,
  type SimEvent,
} from '../sim/types.ts';

const MAX_CATCHUP_STEPS = 5; // never spiral; drop time instead
const HUD_INTERVAL_MS = 66; // ~15Hz, so HUD state does not re-render at 60fps
const RESTART_HOLD_MS = 300;
/** How long a puzzle room freezes on entry (and restart) before the sim
 *  starts stepping, so the player can read the room before it's live. Lives
 *  entirely in this UI layer, not in `sim.ts`: the simulation itself starts
 *  stepping from a blank slate exactly as it always has, so this touches
 *  nothing the solvability harness or recorded solutions depend on. */
const COUNTDOWN_SECONDS = 3;

export interface HudState {
  moonlight: number;
  outcome: OutcomeName;
  /** Why the room failed. Meaningless while outcome !== 'failed'. */
  failReason: FailReasonName;
  hasMoonseed: boolean;
  itemsTaken: number;
  itemsTotal: number;
  paused: boolean;
  roomName: string;
  /** Seconds left before the room goes live, or null once it has. */
  countdown: number | null;
  /** Whichever door is currently in range to activate, if any. */
  doorPrompt: DoorPrompt | null;
  /** True while the player can press E to enter the open win portal. */
  portalPrompt: boolean;
}

/**
 * Owns the game loop and the renderer's lifetime.
 *
 * The renderer is created and disposed here rather than being held in React
 * state, and the effect's cleanup disposes it unconditionally — which is what
 * makes Strict Mode's double-mount safe (brief risk R-07).
 *
 * The simulation runs on a fixed 60Hz accumulator regardless of display refresh,
 * so a 144Hz monitor and a 60Hz phone produce identical play. Rendering happens
 * once per animation frame from whatever the latest state is.
 */
export function useGame(
  containerRef: React.RefObject<HTMLElement | null>,
  room: RoomDef = firstLight,
  character: PlayableCharacter,
  /** Fired with each tick's events (door touches, item pickups, etc). Read
   *  through a ref internally so passing a fresh inline function every
   *  render doesn't restart the game loop the way a `room` change does. */
  onEvents?: (events: readonly SimEvent[]) => void,
  /** Which unlocked parts change this run's verbs (brief §7). Defaults to
   *  the Starter loadout - the same one the solvability harness replays
   *  every recorded solution against - so a caller that doesn't pass one
   *  (every existing call site, and every test) gets exactly that. */
  loadout: Loadout = STARTER_LOADOUT,
  /** Accessibility setting. The default is the canonical 1x timer. */
  assistEnabled = false,
) {
  const simRef = useRef<Simulation | null>(null);
  const pausedRef = useRef(false);
  const touchHeldRef = useRef(0);
  const touchLatchedRef = useRef(0);
  const assistEnabledRef = useRef(assistEnabled);
  const onEventsRef = useRef(onEvents);
  useEffect(() => {
    onEventsRef.current = onEvents;
  }, [onEvents]);
  useEffect(() => {
    assistEnabledRef.current = assistEnabled;
  }, [assistEnabled]);

  /** Feeds touch buttons through the identical bitmask path as the keyboard.
   * Presses are latched so fast taps cannot disappear between 60 Hz samples. */
  const setTouchButton = useCallback((button: number, held: boolean) => {
    if (held) {
      touchHeldRef.current |= button;
      touchLatchedRef.current |= button;
    } else {
      touchHeldRef.current &= ~button;
    }
  }, []);
  const [hud, setHud] = useState<HudState>(() => ({
    moonlight: room.moonlight ?? 90,
    outcome: Outcome.Playing,
    failReason: FailReason.None,
    hasMoonseed: false,
    itemsTaken: 0,
    itemsTotal: room.items.length,
    paused: false,
    roomName: room.name,
    countdown: room.skipCountdown ? null : COUNTDOWN_SECONDS,
    doorPrompt: null,
    portalPrompt: false,
  }));

  // Mutable, not React state: read and written every frame inside the game
  // loop below. `countdownDurationRef` is fixed per room (set once, when the
  // main effect (re)runs); `countdownRef` is what actually counts down and
  // is what both the loop's R-hold restart and this `restart()` (called from
  // the Hud's "Play again"/"Retry" buttons) reset back to it.
  const countdownRef = useRef(room.skipCountdown ? 0 : COUNTDOWN_SECONDS);
  const countdownDurationRef = useRef(room.skipCountdown ? 0 : COUNTDOWN_SECONDS);

  const restart = useCallback(() => {
    simRef.current?.reset();
    pausedRef.current = false;
    countdownRef.current = countdownDurationRef.current;
  }, []);

  const setPaused = useCallback((next: boolean) => {
    pausedRef.current = next;
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const sim = new Simulation(room, loadout);
    simRef.current = sim;
    countdownDurationRef.current = room.skipCountdown ? 0 : COUNTDOWN_SECONDS;
    countdownRef.current = countdownDurationRef.current;
    const renderer = new Renderer(container);
    const keys = new KeyboardSource();
    keys.attach();

    // Load the real Axie after the room is already playable. It is deliberately
    // not awaited: the toolkit and its 8.6 MB manifest must never sit between a
    // judge and Room 1 (brief §15, risk R-03). Until it resolves - or if it never
    // does - the renderer keeps drawing the placeholder.
    const axieAbort = new AbortController();
    let disposed = false;
    void createCharacterAxie(character, renderer.rendererHandle, axieAbort.signal).then((loadedCharacter) => {
      if (disposed) {
        loadedCharacter.dispose();
        return;
      }
      renderer.setCharacter(loadedCharacter);
    });

    // Dev-only Slime Buddy preview (?slime=1), same opt-in shape as
    // ?room=warden-test: additive, never touches the normal gameplay path
    // when the flag is absent. Exposed on window for manual poking from the
    // console (setBounce/setExplode) since there is no in-game UI for it.
    if (new URLSearchParams(location.search).has('slime')) {
      void renderer
        .showSlimeBuddyPreview()
        .then((slimeBuddy) => {
          if (disposed) return;
          (window as unknown as { slimeBuddy?: unknown }).slimeBuddy = slimeBuddy;
        })
        // Strict Mode's throwaway first mount can dispose this renderer before
        // the dynamic import resolves - expected there, not a real failure.
        .catch(() => {});
    }

    // Dev-only Emerald Leaf Emblem preview (?emerald=1), same opt-in shape as
    // ?slime=1 above - a reused model from a separate img2threejs workspace,
    // not built for this game, mounted only when explicitly requested.
    // Exposed on window for manual poking (setSpin/setExplode) since there
    // is no in-game UI for it either.
    if (new URLSearchParams(location.search).has('emerald')) {
      void renderer
        .showEmeraldPreview()
        .then((emerald) => {
          if (disposed) return;
          (window as unknown as { emeraldPreview?: unknown }).emeraldPreview = emerald;
        })
        .catch(() => {});
    }

    let raf = 0;
    let last = performance.now();
    let accumulator = 0;
    let lastHud = 0;
    let restartHeldSince: number | null = null;

    // Pause on blur. The moonlight timer must never drain while the player is
    // not looking at the game (brief §10).
    const onBlur = (): void => {
      pausedRef.current = true;
    };
    const onVisibility = (): void => {
      if (document.hidden) pausedRef.current = true;
    };
    const onKey = (e: KeyboardEvent): void => {
      // Matches `key` as well as `code`, because `code` is not always populated
      // (synthetic events, some remote-desktop and virtual-keyboard stacks). The
      // same reason KeyboardSource carries a fallback table.
      const key = e.key.toLowerCase();
      if (e.code === 'Escape' || e.code === 'KeyP' || key === 'escape' || key === 'p') {
        pausedRef.current = !pausedRef.current;
      }
    };
    window.addEventListener('blur', onBlur);
    window.addEventListener('keydown', onKey);
    document.addEventListener('visibilitychange', onVisibility);

    const frame = (now: number): void => {
      raf = requestAnimationFrame(frame);
      const elapsed = Math.min(now - last, 250);
      last = now;

      // Restart is a held key so a stray tap cannot wipe an attempt in progress.
      if (keys.isHeld('KeyR')) {
        restartHeldSince ??= now;
        if (now - restartHeldSince >= RESTART_HOLD_MS) {
          restart();
          restartHeldSince = null;
          accumulator = 0;
        }
      } else {
        restartHeldSince = null;
      }

      if (!pausedRef.current && countdownRef.current > 0) {
        countdownRef.current = Math.max(0, countdownRef.current - elapsed / 1000);
      }

      // Collected across every step this frame (a slow frame can run several
      // catch-up ticks at once) so a transient render effect - a fireball's
      // beam flash - sees every shot fired this frame, not just the last one.
      const frameEvents: SimEvent[] = [];
      if (countdownRef.current <= 0 && !pausedRef.current && sim.state.outcome === Outcome.Playing) {
        accumulator += elapsed / 1000;
        let steps = 0;
        while (accumulator >= DT && steps < MAX_CATCHUP_STEPS) {
          sim.setMoonlightDrainMultiplier(assistEnabledRef.current ? 0.5 : 1);
          const touchInput = touchHeldRef.current | touchLatchedRef.current;
          touchLatchedRef.current = 0;
          const events = sim.step(keys.sample() | touchInput);
          if (events.length > 0) {
            onEventsRef.current?.(events);
            frameEvents.push(...events);
          }
          accumulator -= DT;
          steps++;
          if (sim.state.outcome !== Outcome.Playing) break;
        }
        if (steps >= MAX_CATCHUP_STEPS) accumulator = 0;
      }

      if (frameEvents.some((event) => event.type === 'cast:create' || event.type === 'cast:destroy')) {
        renderer.playCharacterAttack();
      }

      renderer.render(sim.state, elapsed / 1000, frameEvents);
      audio.handleEvents(frameEvents);
      audio.setDucked(pausedRef.current);
      audio.setLowMoonlight(
        countdownRef.current <= 0 &&
          !pausedRef.current &&
          sim.state.outcome === Outcome.Playing &&
          sim.state.moonlight < LOW_MOONLIGHT_THRESHOLD,
      );

      if (now - lastHud >= HUD_INTERVAL_MS) {
        lastHud = now;
        const s = sim.state;
        setHud({
          moonlight: s.moonlight,
          outcome: s.outcome,
          failReason: s.failReason,
          hasMoonseed: s.player.hasMoonseed,
          itemsTaken: s.items.filter((i) => i.taken).length,
          itemsTotal: s.items.length,
          paused: pausedRef.current,
          roomName: room.name,
          countdown: countdownRef.current > 0 ? countdownRef.current : null,
          doorPrompt: s.doorPrompt,
          portalPrompt: s.portalPrompt,
        });
      }
    };
    raf = requestAnimationFrame(frame);

    return () => {
      disposed = true;
      axieAbort.abort();
      cancelAnimationFrame(raf);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('visibilitychange', onVisibility);
      keys.detach();
      touchHeldRef.current = 0;
      touchLatchedRef.current = 0;
      renderer.dispose();
      simRef.current = null;
      // The alarm loop is a `setInterval` outside React/render state - it
      // must be stopped explicitly, or leaving a room mid-alarm (e.g. via a
      // door) would leave it beeping into the next room for a frame or two.
      audio.setLowMoonlight(false);
    };
    // `loadout` is intentionally excluded: it only ever changes as a
    // consequence of clearing a room, which already remounts this whole
    // hook via `room` (App keys GameView by room.id) - re-running this
    // effect for it too would just be a second, redundant reset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [character, containerRef, room, restart]);

  return { hud, restart, setPaused, setTouchButton };
}
